// Plugin de i18n para Worktime Client
// Migrado de Vue I18n v8 (Vue 2) a v10 (Vue 3)

import type { I18nOptions } from 'vue-i18n'
import { createI18n } from 'vue-i18n'

// Importar mensajes directamente (Vite usa import.meta.glob en lugar de require.context)
import en from '../locale/en.json'
import es from '../locale/es.json'
import it from '../locale/it.json'
import pt from '../locale/pt.json'
import ro from '../locale/ro.json'

const baseMessages = {
  es,
  en,
  it,
  pt,
  ro,
} as const

export type SupportedLocale = keyof typeof baseMessages
export const SUPPORTED_LOCALES = Object.keys(baseMessages) as SupportedLocale[]

function cloneLocaleMessages<T>(messages: T): T {
  return JSON.parse(JSON.stringify(messages)) as T
}

export function normalizeLocale(locale?: string | null): SupportedLocale {
  const normalized =
    typeof locale === 'string' && locale.trim().length > 0
      ? locale.trim().split(/[-_]/)[0].toLowerCase()
      : ''

  if (normalized === 'ca' || normalized === 'eu') {
    return 'es'
  }

  if (SUPPORTED_LOCALES.includes(normalized as SupportedLocale)) {
    return normalized as SupportedLocale
  }

  return 'en'
}

/**
 * Obtiene el locale del navegador
 */
export function getBrowserLocale(): SupportedLocale {
  const navigatorLocale =
    navigator.languages !== undefined
      ? navigator.languages[0]
      : navigator.language

  if (!navigatorLocale) {
    return 'en'
  }

  return normalizeLocale(navigatorLocale)
}

/**
 * Obtiene el locale inicial de la aplicación
 */
function getStartingLocale(): string {
  const browserLocale = getBrowserLocale()
  return normalizeLocale(import.meta.env.VITE_I18N_LOCALE || browserLocale)
}

/**
 * Configuración de i18n
 */
const i18nConfig: I18nOptions = {
  legacy: false, // Usar Composition API
  locale: getStartingLocale(),
  fallbackLocale: 'en',
  messages: {
    es,
    en,
    it,
    pt,
    ro,
  },
  globalInjection: true, // Inyecta $t globalmente
  missingWarn: false, // No advertir sobre traducciones faltantes en desarrollo
  fallbackWarn: false,
}

/**
 * Instancia de i18n
 */
export const i18n = createI18n(i18nConfig)

/**
 * Helper para cambiar el locale
 */
export function setLocale(locale: string): void {
  const globalI18n = i18n.global as any
  globalI18n.locale.value = normalizeLocale(locale)
}

/**
 * Helper para obtener el locale actual
 */
export function getCurrentLocale(): string {
  const globalI18n = i18n.global as any
  return globalI18n.locale.value as string
}

export function resetLocaleMessages(locale?: string | null): void {
  const globalI18n = i18n.global as any
  const locales = locale
    ? [normalizeLocale(locale)]
    : SUPPORTED_LOCALES

  locales.forEach((supportedLocale) => {
    globalI18n.setLocaleMessage(
      supportedLocale,
      cloneLocaleMessages(baseMessages[supportedLocale]),
    )
  })
}

export function mergeCustomLocalization(
  customLocalization?: string | Record<string, unknown> | null,
): void {
  resetLocaleMessages()

  if (!customLocalization) {
    return
  }

  let parsedCustomLocalization: Record<string, unknown> | null = null

  if (typeof customLocalization === 'string') {
    try {
      parsedCustomLocalization = JSON.parse(customLocalization) as Record<
        string,
        unknown
      >
    } catch (error) {
      console.warn('Unable to parse company customLocalization', error)
      return
    }
  } else if (
    typeof customLocalization === 'object' &&
    customLocalization !== null &&
    !Array.isArray(customLocalization)
  ) {
    parsedCustomLocalization = customLocalization
  }

  if (!parsedCustomLocalization) {
    return
  }

  const globalI18n = i18n.global as any

  SUPPORTED_LOCALES.forEach((supportedLocale) => {
    const localeCustomMessages = parsedCustomLocalization?.[supportedLocale]
    if (
      !localeCustomMessages ||
      typeof localeCustomMessages !== 'object' ||
      Array.isArray(localeCustomMessages)
    ) {
      return
    }

    globalI18n.mergeLocaleMessage(supportedLocale, localeCustomMessages)
  })
}
